import type { HomologationStatus, ProductHomologationByStatus } from '@/components/ProductCard/types'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { HOMOLOGATION_STATUS_CONFIG } from '@/constants/homologation'
import { useCountriesContext } from '@/contexts/countries/useCountriesContext'

interface UseManufactureHomologationResult {
  selectedCountry: ReturnType<ReturnType<typeof useCountriesContext>['getCountryByCode']> | null
  countries: ProductHomologationByStatus
  status: HomologationStatus
  hasHomologationData: boolean
  homologatedCountriesCount: number
  countriesCount: number
}

export function useManufactureHomologation(
  homologatedCountries: ProductHomologationByStatus,
): UseManufactureHomologationResult {
  const { getCountryByCode, normalizeCountryCodes } = useCountriesContext()
  const [searchParams] = useSearchParams()

  const selectedCountryCodes = useMemo(
    () => normalizeCountryCodes(searchParams.getAll('homologation_countries')),
    [normalizeCountryCodes, searchParams],
  )
  const selectedCountry = selectedCountryCodes.length === 1
    ? getCountryByCode(selectedCountryCodes[0]) ?? null
    : null
  const selectedCountryCode = selectedCountryCodes.length === 1 ? selectedCountryCodes[0] : null

  const selectedCountryCodeSet = useMemo(() => new Set(selectedCountryCodes), [selectedCountryCodes])

  const countries = useMemo(() => {
    if (!selectedCountryCodeSet.size)
      return homologatedCountries

    return HOMOLOGATION_STATUS_CONFIG.reduce<ProductHomologationByStatus>((filtered, { group }) => {
      filtered[group] = homologatedCountries[group].filter(country => selectedCountryCodeSet.has(country.country_code))
      return filtered
    }, { ok: [], nok: [], der: [], undefined: [] } as ProductHomologationByStatus)
  }, [homologatedCountries, selectedCountryCodeSet])

  const status = useMemo(() => {
    if (!selectedCountryCode)
      return 'UNVERIFIED'

    const statusConfig = HOMOLOGATION_STATUS_CONFIG.find(({ group }) => (
      homologatedCountries[group].some(country => country.country_code === selectedCountryCode)
    ))

    return statusConfig?.status ?? 'UNVERIFIED'
  }, [homologatedCountries, selectedCountryCode])

  const homologatedCountriesCount = countries.ok.length
  const countriesCount = HOMOLOGATION_STATUS_CONFIG.reduce(
    (count, { group }) => count + countries[group].length,
    0,
  )

  return {
    selectedCountry,
    countries,
    status,
    hasHomologationData: countriesCount > 0,
    homologatedCountriesCount,
    countriesCount,
  }
}
